import { useState, useCallback } from 'react';
import { 
  collection, 
  doc, 
  writeBatch, 
  runTransaction, 
  serverTimestamp,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase';
import { PharmacyQueries } from '../models/pharmacyModel';

/**
 * Hook principal para gestión de farmacia
 * Maneja transacciones atómicas de dispensación de insumos
 */
export const usePharmacy = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Transacción atómica para dispensar insumo
   * Descuenta stock Y carga al paciente simultáneamente
   */
  const dispenseSupply = useCallback(async (dispensationData) => {
    setLoading(true);
    setError(null);

    try {
      const {
        productId,
        quantity,
        unitOfMeasure,
        location,
        patientId,
        doctorId,
        nurseId,
        referenceId,
        referenceType,
        purpose = 'medication'
      } = dispensationData;

      const result = await runTransaction(db, async (transaction) => {
        // 1. ENCONTRAR LOTE APROPIADO (FEFO - First Expired, First Out)
        const batchesQuery = query(
          collection(db, 'batches'),
          where('productId', '==', productId),
          where('location', '==', location),
          where('isActive', '==', true),
          where('currentQuantity', '>', 0)
        );
        
        const batchesSnapshot = await getDocs(batchesQuery);
        
        if (batchesSnapshot.empty) {
          throw new Error('No hay stock disponible para este producto');
        }

        // Ordenar por fecha de vencimiento (más cercana primero)
        const batches = batchesSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => a.expirationDate.toMillis() - b.expirationDate.toMillis());

        const selectedBatch = batches[0];
        
        // 2. VALIDAR STOCK SUFICIENTE
        if (selectedBatch.currentQuantity < quantity) {
          throw new Error(`Stock insuficiente. Disponible: ${selectedBatch.currentQuantity}, Solicitado: ${quantity}`);
        }

        // 3. OBTENER INFORMACIÓN DEL PRODUCTO
        const productRef = doc(db, 'products', productId);
        const productDoc = await transaction.get(productRef);
        
        if (!productDoc.exists()) {
          throw new Error('Producto no encontrado');
        }
        
        const product = productDoc.data();

        // 4. CALCULAR COSTOS
        const unitCost = selectedBatch.cost || product.averageCost || 0;
        const totalCost = unitCost * quantity;

        // 5. ACTUALIZAR STOCK DEL LOTE
        const batchRef = doc(db, 'batches', selectedBatch.id);
        const newQuantity = selectedBatch.currentQuantity - quantity;
        
        transaction.update(batchRef, {
          currentQuantity: newQuantity,
          lastTransactionDate: serverTimestamp()
        });

        // 6. REGISTRAR TRANSACCIÓN DE INVENTARIO
        const transactionRef = doc(collection(db, 'inventory_transactions'));
        const transactionData = {
          type: 'dispensation',
          productId,
          batchId: selectedBatch.id,
          quantity: -quantity, // negativo porque es salida
          unitOfMeasure,
          location,
          referenceId,
          referenceType,
          patientId,
          doctorId,
          nurseId,
          purpose,
          cost: unitCost,
          totalCost,
          timestamp: serverTimestamp(),
          notes: `Dispensado para ${referenceType}: ${referenceId}`
        };

        // Manejo especial para sustancias controladas
        if (product.isControlled) {
          transactionData.metadata = {
            isControlledSubstance: true,
            controlledSubstanceLog: {
              authorizedBy: doctorId,
              witnessId: nurseId,
              balanceBefore: selectedBatch.currentQuantity,
              balanceAfter: newQuantity
            }
          };

          // 7. REGISTRAR EN LOG DE SUSTANCIAS CONTROLADAS
          const controlledLogRef = doc(collection(db, 'controlled_substance_log'));
          transaction.set(controlledLogRef, {
            productId,
            batchId: selectedBatch.id,
            transactionId: transactionRef.id,
            action: 'dispensed',
            quantity,
            balanceBefore: selectedBatch.currentQuantity,
            balanceAfter: newQuantity,
            authorizedBy: doctorId,
            witnessedBy: nurseId,
            administeredBy: nurseId,
            patientId,
            timestamp: serverTimestamp(),
            location,
            notes: `Administrado a paciente ${patientId}`
          });
        }

        transaction.set(transactionRef, transactionData);

        // 8. CARGAR AL PACIENTE (Patient Ledger)
        const patientAccountRef = doc(db, 'patient_accounts', patientId);
        const chargesRef = collection(patientAccountRef, 'charges');
        const chargeRef = doc(chargesRef);
        
        transaction.set(chargeRef, {
          type: 'medication',
          productId,
          batchId: selectedBatch.id,
          description: `${product.name} - ${quantity} ${unitOfMeasure}`,
          quantity,
          unitPrice: unitCost,
          totalAmount: totalCost,
          timestamp: serverTimestamp(),
          referenceId,
          referenceType,
          doctorId,
          status: 'pending',
          metadata: {
            administeredBy: nurseId,
            administeredAt: serverTimestamp(),
            location,
            batchNumber: selectedBatch.batchNumber
          }
        });

        // 9. VERIFICAR Y CREAR ALERTA DE STOCK BAJO
        if (newQuantity <= product.minStockLevel) {
          const alertRef = doc(collection(db, 'stock_alerts'));
          transaction.set(alertRef, {
            productId,
            locationId: location,
            alertType: 'low_stock',
            currentStock: newQuantity,
            threshold: product.minStockLevel,
            isResolved: false,
            createdAt: serverTimestamp(),
            message: `Stock bajo para ${product.name} en ${location}. Actual: ${newQuantity}, Mínimo: ${product.minStockLevel}`
          });
        }

        return {
          success: true,
          batchUsed: selectedBatch,
          newQuantity,
          totalCost,
          product: product.name
        };
      });

      setLoading(false);
      return result;

    } catch (err) {
      console.error('Error en dispensación:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, []);

  /**
   * Obtener stock actual de un producto
   */
  const getProductStock = useCallback(async (productId, location = null) => {
    try {
      const batchesQuery = location
        ? query(
            collection(db, 'batches'),
            where('productId', '==', productId),
            where('location', '==', location),
            where('isActive', '==', true)
          )
        : query(
            collection(db, 'batches'),
            where('productId', '==', productId),
            where('isActive', '==', true)
          );

      const snapshot = await getDocs(batchesQuery);
      const batches = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const totalStock = batches.reduce((sum, batch) => sum + batch.currentQuantity, 0);
      
      return {
        totalStock,
        batches: batches.sort((a, b) => a.expirationDate.toMillis() - b.expirationDate.toMillis()),
        locations: [...new Set(batches.map(b => b.location))]
      };
    } catch (err) {
      console.error('Error obteniendo stock:', err);
      throw err;
    }
  }, []);

  /**
   * Buscar productos para autocompletar
   */
  const searchProducts = useCallback(async (searchTerm, category = null) => {
    try {
      let productsQuery = query(
        collection(db, 'products'),
        where('isActive', '==', true)
      );

      // Nota: Firestore no soporta búsquedas de texto parcial nativas
      // En producción, considerar Algolia o implementar búsqueda del lado del cliente
      const snapshot = await getDocs(productsQuery);
      let products = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Filtrado del lado del cliente
      if (searchTerm) {
        const lowerSearch = searchTerm.toLowerCase();
        products = products.filter(product => 
          product.name.toLowerCase().includes(lowerSearch) ||
          product.genericName?.toLowerCase().includes(lowerSearch) ||
          product.brand?.toLowerCase().includes(lowerSearch)
        );
      }

      if (category) {
        products = products.filter(product => product.category === category);
      }

      return products.sort((a, b) => a.name.localeCompare(b.name));
    } catch (err) {
      console.error('Error buscando productos:', err);
      throw err;
    }
  }, []);

  /**
   * Transferir stock entre ubicaciones
   */
  const transferStock = useCallback(async (transferData) => {
    setLoading(true);
    setError(null);

    try {
      const {
        productId,
        batchId,
        quantity,
        fromLocation,
        toLocation,
        reason,
        transferredBy
      } = transferData;

      await runTransaction(db, async (transaction) => {
        // 1. Verificar stock origen
        const batchRef = doc(db, 'batches', batchId);
        const batchDoc = await transaction.get(batchRef);
        
        if (!batchDoc.exists()) {
          throw new Error('Lote no encontrado');
        }

        const batch = batchDoc.data();
        
        if (batch.currentQuantity < quantity) {
          throw new Error('Stock insuficiente para transferencia');
        }

        // 2. Actualizar lote origen
        transaction.update(batchRef, {
          currentQuantity: batch.currentQuantity - quantity,
          lastTransactionDate: serverTimestamp()
        });

        // 3. Buscar o crear lote destino
        const destinationBatchesQuery = query(
          collection(db, 'batches'),
          where('productId', '==', productId),
          where('location', '==', toLocation),
          where('batchNumber', '==', batch.batchNumber),
          where('isActive', '==', true)
        );

        const destinationSnapshot = await getDocs(destinationBatchesQuery);
        
        if (destinationSnapshot.empty) {
          // Crear nuevo lote en destino
          const newBatchRef = doc(collection(db, 'batches'));
          transaction.set(newBatchRef, {
            productId,
            batchNumber: batch.batchNumber,
            manufacturingDate: batch.manufacturingDate,
            expirationDate: batch.expirationDate,
            initialQuantity: quantity,
            currentQuantity: quantity,
            location: toLocation,
            cost: batch.cost,
            supplier: batch.supplier,
            isActive: true,
            createdAt: serverTimestamp(),
            lastTransactionDate: serverTimestamp()
          });
        } else {
          // Actualizar lote existente
          const destBatchRef = doc(db, 'batches', destinationSnapshot.docs[0].id);
          const destBatch = destinationSnapshot.docs[0].data();
          transaction.update(destBatchRef, {
            currentQuantity: destBatch.currentQuantity + quantity,
            lastTransactionDate: serverTimestamp()
          });
        }

        // 4. Registrar transacción de transferencia
        const transactionRef = doc(collection(db, 'inventory_transactions'));
        transaction.set(transactionRef, {
          type: 'transfer',
          productId,
          batchId,
          quantity: -quantity,
          unitOfMeasure: batch.unitOfMeasure,
          location: fromLocation,
          referenceId: toLocation,
          referenceType: 'transfer',
          purpose: 'stock_transfer',
          cost: batch.cost,
          totalCost: batch.cost * quantity,
          timestamp: serverTimestamp(),
          notes: `Transferido a ${toLocation}: ${reason}`,
          metadata: {
            transferredBy,
            toLocation,
            fromLocation
          }
        });

        // 5. Transacción de entrada en destino
        const destTransactionRef = doc(collection(db, 'inventory_transactions'));
        transaction.set(destTransactionRef, {
          type: 'transfer',
          productId,
          batchId,
          quantity: quantity, // positivo porque es entrada
          unitOfMeasure: batch.unitOfMeasure,
          location: toLocation,
          referenceId: fromLocation,
          referenceType: 'transfer',
          purpose: 'stock_transfer',
          cost: batch.cost,
          totalCost: batch.cost * quantity,
          timestamp: serverTimestamp(),
          notes: `Recibido desde ${fromLocation}: ${reason}`,
          metadata: {
            transferredBy,
            fromLocation,
            toLocation
          }
        });
      });

      setLoading(false);
      return { success: true };

    } catch (err) {
      console.error('Error en transferencia:', err);
      setError(err.message);
      setLoading(false);
      throw err;
    }
  }, []);

  return {
    loading,
    error,
    dispenseSupply,
    getProductStock,
    searchProducts,
    transferStock
  };
};
